/**
 * BNPL data screen (ERS-5, rebuilt as screen 08 in ERS-78)
 *
 * Route: `/webAppOnboarding/insurance-bnpl-checkout`. Thin screen over `useBnplCheckoutScreen` (all
 * logic): personal-data form only. Consents, the busy overlay and the legal drawer live on the payment
 * screen (09).
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` → `OnboardingAuthGuard` → `OnboardingCompletedGuard`.
 * - Auth: Firebase user required via `OnboardingAuthGuard` (callables use signed-in session).
 * - Onboarding state: completed patients redirected to install by `OnboardingCompletedGuard`.
 * - Screen-local: `useInsuranceExplainBillingGuard('bnpl')` (inside the hook, no `suppressedRef` —
 *   this screen never writes `billingType`); `useSaveOnboardingStep('insurance_bnpl_checkout')`
 *   (inside the hook).
 */

import styled from 'styled-components';
import { colors } from '../../../styles/colors';
import { space } from '../../../styles/onboardingUiTokens';
import { OnboardingHeroBanner } from '../components/content/OnboardingHeroBanner';
import { LoadingSpinner } from '../components/elements/LoadingSpinner';
import { Notice } from '../components/elements/Notice';
import { BirthdayInput } from '../components/forms/BirthdayInput';
import { TextInput } from '../components/forms/TextInput';
import { OnboardingBody } from '../components/layout/OnboardingBody';
import { OnboardingFooter } from '../components/layout/OnboardingFooter';
import { OnboardingHeader } from '../components/layout/OnboardingHeader';
import { OnboardingLayout } from '../components/layout/OnboardingLayout';
import { Body } from '../components/typography/Body';
import { H1 } from '../components/typography/HeadingLevels';
import { useBnplCheckoutScreen } from '../hooks/useBnplCheckoutScreen';
import {
  BNPL_CHECKOUT_FIELD_LABELS,
  BNPL_CHECKOUT_GENERIC_ERROR_NOTICE,
  BNPL_CHECKOUT_PHONE_HINT,
  BNPL_CHECKOUT_SUBTITLE,
  BNPL_DATA_CTA,
  BNPL_DATA_FIELDS_ERROR_NOTICE,
  BNPL_DATA_HEADLINE,
} from '../insurance/bnplCopy';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: true,
} as const;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
`;

const DynamicLoadingArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
  width: 100%;
`;

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.md};
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${space.sm};
`;

export function WebOnboardingInsuranceBnplCheckoutScreen() {
  const checkout = useBnplCheckoutScreen();
  const {
    insurance,
    insuranceLoading,
    insuranceError,
    phoneDisplay,
    form,
    charErrors,
    birthdayError,
    setField,
    fieldsError,
    fieldsRef,
    saveError,
    forwardDisabled,
    forwardInactive,
    forwardBusy,
    handleBack,
    handleForward,
  } = checkout;

  const bodyContent = (() => {
    if (insuranceLoading) {
      return (
        <DynamicLoadingArea>
          <LoadingSpinner color={colors.primary} size={32} />
        </DynamicLoadingArea>
      );
    }

    if (insuranceError) {
      return (
        <Notice preset="warning" title="Krankenkasse">
          {insuranceError}
        </Notice>
      );
    }

    if (!insurance) {
      return (
        <Notice preset="warning" title="Krankenkasse">
          Bitte wähle zuerst deine Krankenkasse aus.
        </Notice>
      );
    }

    return (
      <ContentColumn>
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
          <H1>{BNPL_DATA_HEADLINE}</H1>
          <Body variant="document" tone="muted">
            {BNPL_CHECKOUT_SUBTITLE}
          </Body>
        </div>

        <div ref={fieldsRef}>
          {fieldsError ? (
            <Notice preset="warning" title="Bitte vervollständigen">
              {BNPL_DATA_FIELDS_ERROR_NOTICE}
            </Notice>
          ) : null}
          <FieldStack>
            <FieldRow>
              <TextInput
                name="firstName"
                autoComplete="given-name"
                label={BNPL_CHECKOUT_FIELD_LABELS.firstName}
                value={form.firstName}
                onChange={(value) => setField('firstName', value)}
                error={charErrors.firstName}
              />
              <TextInput
                name="lastName"
                autoComplete="family-name"
                label={BNPL_CHECKOUT_FIELD_LABELS.lastName}
                value={form.lastName}
                onChange={(value) => setField('lastName', value)}
                error={charErrors.lastName}
              />
            </FieldRow>
            <TextInput
              name="street"
              autoComplete="address-line1"
              label={BNPL_CHECKOUT_FIELD_LABELS.street}
              value={form.street}
              onChange={(value) => setField('street', value)}
              error={charErrors.street}
            />
            <FieldRow>
              <TextInput
                name="zip"
                autoComplete="postal-code"
                label={BNPL_CHECKOUT_FIELD_LABELS.zip}
                value={form.zip}
                onChange={(value) => setField('zip', value)}
                error={charErrors.zip}
              />
              <TextInput
                name="city"
                autoComplete="address-level2"
                label={BNPL_CHECKOUT_FIELD_LABELS.city}
                value={form.city}
                onChange={(value) => setField('city', value)}
                error={charErrors.city}
              />
            </FieldRow>
            <BirthdayInput
              legend={BNPL_CHECKOUT_FIELD_LABELS.birthday}
              value={form.birthday}
              onChange={(value) => setField('birthday', value)}
              error={birthdayError}
            />
            <TextInput
              label={BNPL_CHECKOUT_FIELD_LABELS.phone}
              value={phoneDisplay}
              disabled
              readOnly
              hint={BNPL_CHECKOUT_PHONE_HINT}
            />
            <TextInput
              type="email"
              name="email"
              autoComplete="email"
              label={BNPL_CHECKOUT_FIELD_LABELS.email}
              value={form.email}
              onChange={(value) => setField('email', value)}
              error={charErrors.email}
            />
          </FieldStack>
        </div>

        {saveError ? (
          <Notice preset="warning" title="Konnte nicht abgeschlossen werden">
            {BNPL_CHECKOUT_GENERIC_ERROR_NOTICE}
          </Notice>
        ) : null}
      </ContentColumn>
    );
  })();

  return (
    <OnboardingLayout
      currentStep={5}
      totalSteps={7}
      forwardLabel={BNPL_DATA_CTA}
      forwardDisabled={forwardDisabled}
      forwardInactive={forwardInactive}
      forwardBusy={forwardBusy}
      onBack={handleBack}
      onForward={handleForward}
    >
      <OnboardingHeader />
      <OnboardingHeroBanner />
      <OnboardingBody>
        <div style={{ paddingBottom: space.xl }}>{bodyContent}</div>
      </OnboardingBody>
      <OnboardingFooter />
    </OnboardingLayout>
  );
}
